import React, { type MouseEvent } from 'react';
import { Handle, Position, useReactFlow, useStore, type NodeProps, type ReactFlowState } from 'reactflow';
import { TruncatedText } from 'components/base/TruncatedText';
import type { FlowChildNode, FlowNodeData } from '../../../edit/flowGraph';
import { shownDefinitionName } from './shownDefinitionName';
import { shownDescription } from './shownDescription';

const SubflowGroupNode = ({ data, id }: NodeProps<FlowNodeData>) => {
  const { label, onExpandNode, activated_handles = [] } = data;
  const fullNode = useReactFlow().getNode(id) as FlowChildNode | undefined;
  const placementDescription = useStore((state: ReactFlowState) => (state.nodeInternals.get(id) as FlowChildNode | undefined)?.description);
  const definitionName = shownDefinitionName(fullNode, label);
  const description = shownDescription({ description: placementDescription, data });
  // A nullable column, filled from the node type's default_handles on upsert, so this guards older rows only.
  const handles = data.handles ?? [];

  const handleCollapse = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onExpandNode) {
      onExpandNode(id);
    }
  };

  return (
    <div
      className="subflow-group-node"
      style={{
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        position: 'relative',
      }}
    >
      <div className="subflow-group-header" style={{ pointerEvents: 'all', position: 'relative', zIndex: 10 }}>
        <div className="subflow-group-title">
          <TruncatedText text={definitionName ? `${label} · ${definitionName}` : (label ?? '')}>
            {label}
            {definitionName && <span className="subflow-group-definition"> · {definitionName}</span>}
          </TruncatedText>
          {description && (
            <div className="subflow-group-description">
              <TruncatedText text={description} />
            </div>
          )}
        </div>
        <button className="subflow-group-collapse-btn" onClick={handleCollapse} style={{ pointerEvents: 'all' }} title="Collapse subflow">
          −
        </button>
      </div>
      <Handle type="target" position={Position.Left} />
      {handles.length > 0 && (
        <div className="subflow-group-handles" style={{ pointerEvents: 'all' }}>
          {handles.map((handle, index) => {
            const isActivated = activated_handles.some((h) => h === handle.id);
            return (
              <div className="handle-wrapper" key={handle.id || index}>
                <Handle id={handle.id} type="source" position={Position.Right} className={`handle ${isActivated ? 'activated' : ''}`} />
                <span className="handle-label me-2">{handle.label}</span>
              </div>
            );
          })}
        </div>
      )}
      {handles.length === 0 && <Handle type="source" position={Position.Right} />}
    </div>
  );
};

export default SubflowGroupNode;
